import { z } from 'zod';
import { coercedBoolean } from './helpers';
/**
 * Schemas for organization management API keys (KIR-3746).
 *
 * A management key is a **Descope Access Key**. Descope owns authentication,
 * tenant scoping, the role snapshot, and expiry; the local `api_keys` row is a
 * display mirror only (`key_type = 'management'`) whose `keyHash` / `keyPrefix`
 * are sentinels that are never authenticated against.
 *
 * The plaintext access key is returned exactly once, by `create`. Nothing else
 * on this surface can ever hand it back.
 */
export const managementApiKeySchema = z.object({
    /** Local mirror row id — the id used to revoke. */
    id: z.string(),
    /** Descope Access Key id — the source of truth this row mirrors. */
    descopeAccessKeyId: z.string(),
    /** First 10 characters of the Descope access-key id. Display only. */
    keyPrefix: z.string(),
    name: z.string().nullable(),
    createdAt: z.coerce.date(),
    expiresAt: z.coerce.date().nullable(),
    lastUsedAt: z.coerce.date().nullable(),
    isActive: z.boolean(),
    /** Local user id of whoever minted the key. */
    createdByUserId: z.string().nullable(),
    /** Display name of the creator, joined from `users`. */
    createdByName: z.string().nullable(),
    createdByEmail: z.string().nullable(),
});
export const managementApiKeyListSchema = z.array(managementApiKeySchema);
/**
 * Revoked keys are hidden by default so the Settings list shows live keys only.
 */
export const listManagementApiKeysQuerySchema = z.object({
    includeRevoked: coercedBoolean().optional().default(false),
});
export const createManagementApiKeyRequestSchema = z.object({
    name: z.string().trim().min(1).max(100),
    /**
     * Optional absolute expiry. Omitted means the key never expires.
     *
     * Must be in the future: Descope accepts a past `expireTime` and mints a key
     * that is already dead, which would return 201 with a one-time plaintext the
     * caller can never use, plus a mirror row claiming to be active.
     */
    expiresAt: z.coerce
        .date()
        .refine(value => value.getTime() > Date.now(), {
        message: 'expiresAt must be in the future',
    })
        .optional(),
});
/**
 * The mirror row plus the plaintext Descope access key. `accessKey` is present
 * on this response and nowhere else — it is never stored and never re-readable.
 */
export const createManagementApiKeyResponseSchema = managementApiKeySchema.extend({
    accessKey: z.string(),
});
export const revokeManagementApiKeyResponseSchema = z.object({
    id: z.string(),
});
//# sourceMappingURL=management-api-keys.js.map